import { FormController } from "$root/third-party-integrations/react-hook-form";
import { valueByPath } from "$root/utils";
import type { StylableProps } from "@mdotm/shadcn/legacy/components";
import { FormField, Label, NumberInput, type NumberInputProps } from "@mdotm/shadcn/mdotm/components";
import { type NodeOrFn, renderNodeOrFn } from "@mdotm/shadcn/legacy/react-extensions";
import type { Control, FieldValues, FormState, Path } from "react-hook-form";

export type NullableNumberFieldProps<T extends FieldValues> = StylableProps & {
	control: Control<T, any>;
	formState: FormState<T>;
	name: Path<T>;
	label: NodeOrFn<{ htmlFor: string }>;
	hideBanner?: boolean;
} & Omit<NumberInputProps, "value">;

export function NullableNumberField<T extends FieldValues>({
	label,
	name,
	formState,
	control,
	className,
	style,
	hideBanner = false,
	...forward
}: NullableNumberFieldProps<T>): React.JSX.Element {
	return (
		<FormField
			className={className}
			style={style}
			label={({ htmlFor }) =>
				typeof label === "string" ? (
					<Label htmlFor={htmlFor} className="mb-px">
						{label}
					</Label>
				) : (
					renderNodeOrFn(label, { htmlFor })
				)
			}
			error={hideBanner ? undefined : (valueByPath(formState.errors, name) as { message?: string })?.message}
		>
			{(fieldProps) => (
				<FormController
					control={control}
					name={name}
					render={({ field: { ref, ...controllerProps } }) => (
						<NumberInput innerRef={ref} {...fieldProps} {...controllerProps} {...forward} />
					)}
				/>
			)}
		</FormField>
	);
}
